import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Dialogo from '@/components/Dialogo';
import { validarEtapa } from '@/leads/regras';
import { dataDeRetornoPadrao } from '@/leads/retorno';
import { ETAPA_AO_CANCELAR } from '@/agenda/cancelamento';

/**
 * ★★ WI — A BAIXA "FALTOU" PEDE O PRÓXIMO CONTATO, OBRIGATORIAMENTE.
 *
 * Palavras do dono: *"quando a gente colocar que a pessoa faltou, a gente tem que colocar
 * para gerar um novo contato, obrigatoriamente. (…) senão vai ficar cliente perdido."*
 *
 * ★ E não era teoria. Medido em `docs/wi-medicao.md` §3: das 13 baixas "faltou" que uma
 * pessoa apertou, **10** deixaram o lead sem nenhuma tarefa aberta; refinando por lead
 * distinto, **7 de 9** ficaram sem tarefa **e** sem consulta futura. E **554 dos 556** leads
 * em "Não compareceu" não tinham próximo contato nenhum.
 *
 * ★ UM CAMPO SÓ, de propósito. "Não compareceu" não tem motivo (decisão da VG) e a baixa não
 * pede código nem valor — quem faltou não sentou na cadeira. Pedir mais do que a data
 * encareceria o gesto de maior volume (740 faltas no histórico) sem comprar nada.
 *
 * ★ A data nasce sugerida pela CADÊNCIA da clínica, editável — o padrão só poupa digitação,
 * e é a mesma função que o diálogo do funil usa (`dataDeRetornoPadrao`).
 */
export default function DialogoBaixaFalta({
  aberto = false,
  lead = null,
  consulta = null,
  clinica = null,
  ocupado = false,
  aoFechar,
  aoConfirmar,
}) {
  const [retorno, setRetorno] = useState('');
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    const padrao = dataDeRetornoPadrao(lead, ETAPA_AO_CANCELAR, clinica?.config ?? null);
    setRetorno(format(padrao, "yyyy-MM-dd'T'HH:mm"));
    setErro(null);
  }, [aberto, lead?.id]);

  if (!aberto) return null;

  async function enviar(e) {
    e.preventDefault();
    const retornoEm = retorno ? new Date(retorno) : null;
    /**
     * ★ A MESMA PORTA DO FUNIL: `validarEtapa('nao_compareceu', …)`. Roda aqui para dar
     * mensagem clara; e como a exigência do próximo contato NÃO é imponível pelas rules
     * (a tarefa é documento irmão no mesmo lote), este cliente é o único lugar onde ela
     * existe — o que está dito de frente em `agendamentosService.registrarComparecimento`.
     */
    const problema = validarEtapa(ETAPA_AO_CANCELAR, { ...lead, retornoEm });
    if (problema) { setErro(problema); return; }
    setErro(null);
    try {
      await aoConfirmar?.(retornoEm);
    } catch (falha) {
      setErro(falha?.message ?? 'Não foi possível registrar a falta.');
    }
  }

  return (
    <Dialogo
      titulo={`Faltou — ${lead?.nome || consulta?.leadNome || 'paciente'}`}
      aberto={aberto}
      aoFechar={aoFechar}
    >
      <form className="dialogo-corpo" onSubmit={enviar}>
        <div className="campo">
          <label htmlFor="falta-retorno">Próximo contato em</label>
          <input
            id="falta-retorno"
            type="datetime-local"
            required
            disabled={ocupado}
            value={retorno}
            onChange={(e) => setRetorno(e.target.value)}
          />
          <p className="campo-ajuda">
            Vira um próximo contato em Próximos contatos. Sem ele o lead fica em "Não
            compareceu" e ninguém volta a ligar — foi o que aconteceu com 554 dos 556 leads
            que já estão nessa coluna.
          </p>
        </div>

        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={aoFechar} disabled={ocupado}>
            Cancelar
          </button>
          <button type="submit" className="btn-primario" disabled={ocupado}>
            {ocupado ? 'Salvando…' : 'Registrar a falta'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
